import graphql__17824db0aaa8785b038d from "./__generated__/CreateMailingListDialogMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import Section from "../form/Section";
import Field from "../form/Field";
import Row from "../form/Row";
import Label from "../form/Label";
import * as yup from "yup";
import FormDialog from "../dialog/FormDialog";
import type {CreateMailingListDialogMutation} from "./__generated__/CreateMailingListDialogMutation.graphql";
import {useNavigate} from "react-router-dom";
import {useRelayEnvironment} from "react-relay/hooks";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";
import {useIntl} from "react-intl";
import MailingListCriterionFields from "./MailingListCriterionFields";

type Props = {
    handleCloseDialog: () => void
}

const CreateMailingListDialog: React.FC<Props> = ({handleCloseDialog}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const navigate = useNavigate();
    const {addError} = useErrorContext();

    const handleSubmission = (values) => {
        const input: {
            name: string,
            description: string,
            type: string,
            criterion?: {conditions: {attributeName: string, operator: string, value: string | null}[]}
        } = {
            name: values.name,
            description: values.description,
            type: values.type,
        };

        if (values.type === 'DYNAMIC') {
            input.criterion = {
                conditions: [{
                    attributeName: values.attributeName,
                    operator: values.operator,
                    value: values.operator === 'EQUALS' ? values.value : null,
                }],
            };
        }

        commitMutationWithErrorHandling<CreateMailingListDialogMutation>(
            relayEnvironment,
            {
                mutation: graphql__17824db0aaa8785b038d,
                variables: {
                    input: input
                },
                onCompleted: (response, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    handleCloseDialog();
                    navigate('/marketing/lists/' + response.createMailingList?.id)
                },
                onError: (error) => {
                    console.error(error);
                    handleCloseDialog();
                }
            },
            addError,
            {
                customErrorMessage: intl.formatMessage({id: 'errors.failedToCreateMailingList'})
            }
        )
    }

    const initialValues = {
        name: '',
        description: '',
        type: 'STATIC',
        attributeName: '',
        operator: 'EQUALS',
        value: '',
    }

    const validationSchema = yup.object().shape({
        name: yup.string()
            .min(2, intl.formatMessage({id: 'validation.field.nameTooShort'}))
            .max(255, intl.formatMessage({id: 'validation.field.nameTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
        description: yup.string()
            .max(1000, intl.formatMessage({id: 'validation.field.descriptionTooLong'})),
        type: yup.string().oneOf(['STATIC', 'DYNAMIC']).required(),
        attributeName: yup.string().when('type', {
            is: 'DYNAMIC',
            then: yup.string().required(intl.formatMessage({id: 'validation.field.required'})),
        }),
        operator: yup.string().when('type', {
            is: 'DYNAMIC',
            then: yup.string().oneOf(['EQUALS', 'EXISTS']).required(),
        }),
        value: yup.string().when(['type', 'operator'], {
            is: (type, operator) => type === 'DYNAMIC' && operator === 'EQUALS',
            then: yup.string().required(intl.formatMessage({id: 'validation.field.required'})),
        }),
    });

    return (
        <FormDialog handleCloseDialog={handleCloseDialog}
                    handleSubmission={handleSubmission}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: 'dialog.CreateMailingListDialog.headline'})}
                    submitLabel={intl.formatMessage({id: 'button.create'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.CreateMailingListDialog.nameLabel'})}</Label>
                    <Field name="name" type="text" autoFocus={true} placeholder={intl.formatMessage({id: 'dialog.CreateMailingListDialog.namePlaceholder'})}/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.CreateMailingListDialog.descriptionLabel'})}</Label>
                    <Field name="description" type="text" placeholder={intl.formatMessage({id: 'dialog.CreateMailingListDialog.descriptionPlaceholder'})}/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.CreateMailingListDialog.typeLabel'})}</Label>
                    <Field name="type" as="select">
                        <option value="STATIC">{intl.formatMessage({id: 'mailingList.type.static'})}</option>
                        <option value="DYNAMIC">{intl.formatMessage({id: 'mailingList.type.dynamic'})}</option>
                    </Field>
                </Row>
                <MailingListCriterionFields/>
            </Section>
        </FormDialog>
    )
}

export default CreateMailingListDialog;
